<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useEOSTable } from '@/composables/eos/eos-table'
import type { ISiteDABidForm } from '@/types/eos/forms'
import type { ISiteDABid } from '@/types/eos/Table'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { type MaskInputOptions } from 'maska'
import { vMaska } from 'maska/vue'
import { reactive, ref, toRef, useTemplateRef, type Ref } from 'vue'

const open = defineModel<boolean>()
const props = defineProps<{ site: ISiteIdentifier; siteDaBid?: ISiteDABid; nbpt?: string }>()

// Prevents button from being clicked after mutation finishes
const updating = ref<boolean>(false)

const { siteDABidMutation } = useEOSTable()
const { mutateAsync: updateSiteDABid, isPending, reset: resetMutation } = siteDABidMutation

const formRef = useTemplateRef<FormInstance>('formRef')
const form = reactive<ISiteDABidForm>({
  price_usd: '',
  load_mw: '',
  site_id: '',
  pr_strike_price_usd: undefined,
  pr_load_mw: undefined,
  energy_market_non_curtailable_load_mw: undefined,
  ancillary_services_market_non_curtailable_load_mw: undefined,
})

const priceUSDUnmasked = toRef(form, 'price_usd')
const loadMWUnmasked = toRef(form, 'load_mw')
const prStrikePriceUSDUnmasked = toRef(form, 'pr_strike_price_usd')
const prLoadMWUnmasked = toRef(form, 'pr_load_mw')
const energyMarketNonCurtailableLoadMWUnmasked = toRef(form, 'energy_market_non_curtailable_load_mw')
const ancillaryServicesMarketNonCurtailableLoadMWUnmasked = toRef(
  form,
  'ancillary_services_market_non_curtailable_load_mw'
)

const formRules = reactive<FormRules>({
  price_usd: [
    {
      required: true,
      type: 'string',
      message: 'Please enter price in USD.',
      trigger: ['change', 'blur'],
    },
  ],
  load_mw: [
    {
      required: true,
      type: 'string',
      message: 'Please enter load (position) in MW.',
      trigger: ['change', 'blur'],
    },
  ],
})

async function update() {
  try {
    await formRef.value?.validate()
  } catch {
    // validate will throw on errors
    updating.value = false
    return
  }

  try {
    updating.value = true
    await updateSiteDABid(form)
    ElMessage.success(`Successfully updated site DA bid for ${props.site.site_name}.`)
  } catch {
    ElMessage.error(`Failed to update site DA bid for ${props.site.site_name}.`)
  } finally {
    open.value = false
  }
}

/**
 * Handle open, set initial form state if site DA already exists.
 */
const handleOpen = () => {
  form.site_id = props.site.site_id!
  if (props.siteDaBid) {
    Object.assign(form, {
      price_usd: props.siteDaBid?.price_usd,
      load_mw: props.siteDaBid?.load_mw,
      pr_strike_price_usd: props.siteDaBid?.pr_strike_price_usd,
      pr_load_mw: props.siteDaBid?.pr_load_mw,
      energy_market_non_curtailable_load_mw: props.siteDaBid?.energy_market_non_curtailable_load_mw,
      ancillary_services_market_non_curtailable_load_mw:
        props.siteDaBid?.ancillary_services_market_non_curtailable_load_mw,
    })
    priceUSDMasked.value = form.price_usd
    loadMWMasked.value = form.load_mw
    prStrikePriceUSDMasked.value = form.pr_strike_price_usd ?? ''
    prLoadMWMasked.value = form.pr_load_mw ?? ''
    energyMarketNonCurtailableLoadMWMasked.value = form.energy_market_non_curtailable_load_mw ?? ''
    ancillaryServicesMarketNonCurtailableLoadMWMasked.value =
      form.ancillary_services_market_non_curtailable_load_mw ?? ''
  }
}

/**
 * Handle close, reset mutation and updating state.
 */
const handleClosed = () => {
  resetMutation()
  priceUSDMasked.value = ''
  loadMWMasked.value = ''
  prStrikePriceUSDMasked.value = ''
  prLoadMWMasked.value = ''
  energyMarketNonCurtailableLoadMWMasked.value = ''
  ancillaryServicesMarketNonCurtailableLoadMWMasked.value = ''
  formRef.value?.resetFields()
  updating.value = false
}

/**
 * Formats load MW input on blur.
 * Pads with zeros after the decimal up to a precision of three.
 */
const formatLoadMw = (loadRef: Ref<string>) => {
  if (loadRef.value) {
    const num = parseFloat(loadRef.value.replace(',', ''))
    loadRef.value = isNaN(num) ? '0.000' : num.toFixed(3)
  } else {
    loadRef.value = ''
  }
}

/**
 * Formats price after input.
 * Pads with zeros after the decimal up to a precision of two.
 */
const formatPrice = (priceRef: Ref<string>) => {
  if (priceRef.value) {
    const num = parseFloat(priceRef.value.replace(/[$,]/g, ''))
    priceRef.value = isNaN(num) ? '0.00' : num.toFixed(2)
  } else {
    priceRef.value = ''
  }
}

// Wrapper functions to handle the refs properly
const handlePriceBlur = () => formatPrice(priceUSDMasked)
const handleLoadMwBlur = () => formatLoadMw(loadMWMasked)
const handlePrStrikePriceBlur = () => formatPrice(prStrikePriceUSDMasked)
const handlePrLoadMwBlur = () => formatLoadMw(prLoadMWMasked)
const handleEnergyMarketNonCurtailableLoadMwBlur = () => formatLoadMw(energyMarketNonCurtailableLoadMWMasked)
const handleAncillaryServicesMarketNonCurtailableLoadMwBlur = () =>
  formatLoadMw(ancillaryServicesMarketNonCurtailableLoadMWMasked)

const priceUSDMasked = ref('')
const prStrikePriceUSDMasked = ref('')
const priceUSDMaskOptions = reactive<MaskInputOptions>({
  mask: '0.99',
  number: {
    fraction: 2,
    locale: 'en-US',
    unsigned: true,
  },
  postProcess: (val) => (val ? `$${val}` : ''),
})

const loadMWMasked = ref('')
const prLoadMWMasked = ref('')
const energyMarketNonCurtailableLoadMWMasked = ref('')
const ancillaryServicesMarketNonCurtailableLoadMWMasked = ref('')
const loadMWMaskOptions = reactive<MaskInputOptions>({
  mask: '0.999',
  number: {
    fraction: 3,
    locale: 'en-US',
    unsigned: true,
  },
})

// Maska requires exposure of directive-bound values
defineExpose({
  priceUSDUnmasked,
  loadMWUnmasked,
  prStrikePriceUSDUnmasked,
  prLoadMWUnmasked,
  energyMarketNonCurtailableLoadMWUnmasked,
  ancillaryServicesMarketNonCurtailableLoadMWUnmasked,
})
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    :title="`Update site DA bid for ${site.site_name}`"
    :show-close="false"
    :close-on-click-modal="false"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <el-form
      ref="formRef"
      :model="form"
      label-width="auto"
      label-position="left"
      :rules="formRules"
      :disabled="updating"
    >
      <el-form-item label="NBPT Price">
        <el-input :value="nbpt" readonly disabled class="text-center">
          <template #suffix>$</template>
        </el-input>
      </el-form-item>
      <el-form-item label="Price" prop="price_usd">
        <el-input
          v-model="priceUSDMasked"
          v-maska:priceUSDUnmasked.unmasked="priceUSDMaskOptions"
          class="w-full"
          placeholder="0.00"
          clearable
          @blur="handlePriceBlur"
          @clear="priceUSDUnmasked = ''"
        >
          <template #suffix>$</template>
        </el-input>
      </el-form-item>
      <el-form-item label="Load" prop="load_mw">
        <el-input
          v-model="loadMWMasked"
          v-maska:loadMWUnmasked.unmasked="loadMWMaskOptions"
          class="w-full"
          placeholder="0.000"
          clearable
          @blur="handleLoadMwBlur"
          @clear="loadMWUnmasked = ''"
        >
          <template #suffix>MW</template>
        </el-input>
      </el-form-item>
      <el-form-item label="PR Strike Price" prop="pr_strike_price_usd">
        <el-input
          v-model="prStrikePriceUSDMasked"
          v-maska:prStrikePriceUSDUnmasked.unmasked="priceUSDMaskOptions"
          class="w-full"
          placeholder="0.00"
          clearable
          @blur="handlePrStrikePriceBlur"
          @clear="prStrikePriceUSDUnmasked = ''"
        >
          <template #suffix>$</template>
        </el-input>
      </el-form-item>
      <el-form-item label="PR Load" prop="pr_load_mw">
        <el-input
          v-model="prLoadMWMasked"
          v-maska:prLoadMWUnmasked.unmasked="loadMWMaskOptions"
          class="w-full"
          placeholder="0.000"
          clearable
          @blur="handlePrLoadMwBlur"
          @clear="prLoadMWUnmasked = ''"
        >
          <template #suffix>MW</template>
        </el-input>
      </el-form-item>
      <el-form-item label="Energy Market Non-Curtailable Load" prop="energy_market_non_curtailable_load_mw">
        <el-input
          v-model="energyMarketNonCurtailableLoadMWMasked"
          v-maska:energyMarketNonCurtailableLoadMWUnmasked.unmasked="loadMWMaskOptions"
          class="w-full"
          placeholder="0.000"
          clearable
          @blur="handleEnergyMarketNonCurtailableLoadMwBlur"
          @clear="energyMarketNonCurtailableLoadMWUnmasked = ''"
        >
          <template #suffix>MW</template>
        </el-input>
      </el-form-item>
      <el-form-item label="ASM Non-Curtailable Load" prop="ancillary_services_market_non_curtailable_load_mw">
        <el-input
          v-model="ancillaryServicesMarketNonCurtailableLoadMWMasked"
          v-maska:ancillaryServicesMarketNonCurtailableLoadMWUnmasked.unmasked="loadMWMaskOptions"
          class="w-full"
          placeholder="0.000"
          clearable
          @blur="handleAncillaryServicesMarketNonCurtailableLoadMwBlur"
          @clear="ancillaryServicesMarketNonCurtailableLoadMWUnmasked = ''"
        >
          <template #suffix>MW</template>
        </el-input>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="updating" @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="updating" :disabled="!isPending && updating" @click="update">
        Confirm
      </el-button>
    </template>
  </el-dialog>
</template>
